import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { QuoteStatusStats, QuoteStatus } from '../types';

interface QuoteStatsBarProps {
  stats: QuoteStatusStats[];
  className?: string;
  /** When true, stats are based on a subset of quotes and may not be fully accurate */
  isApproximate?: boolean;
}

const statusConfig: Record<
  QuoteStatus | 'total',
  { i18nKey: string; valueColor?: string }
> = {
  preparation: { i18nKey: 'stats.preparation', valueColor: 'text-gray-900' },
  sent: { i18nKey: 'stats.sent', valueColor: 'text-yellow-600' },
  'waiting-po': { i18nKey: 'stats.waitingPo', valueColor: 'text-yellow-600' },
  approved: { i18nKey: 'stats.approved', valueColor: 'text-green-600' },
  confirmed: { i18nKey: 'stats.confirmed', valueColor: 'text-green-600' },
  inprogress: { i18nKey: 'stats.inProgress', valueColor: 'text-blue-600' },
  'invoice-ready': { i18nKey: 'stats.invoiceReady', valueColor: 'text-blue-600' },
  'partially-invoiced': { i18nKey: 'stats.partiallyInvoiced', valueColor: 'text-blue-600' },
  invoiced: { i18nKey: 'stats.invoiced', valueColor: 'text-green-600' },
  cancelled: { i18nKey: 'stats.cancelled', valueColor: 'text-gray-500' },
  rejected: { i18nKey: 'stats.rejected', valueColor: 'text-red-600' },
  expired: { i18nKey: 'stats.expired', valueColor: 'text-orange-600' },
  total: { i18nKey: 'stats.total', valueColor: 'text-gray-900' },
};

function formatStatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function QuoteStatsBar({ stats, className, isApproximate }: QuoteStatsBarProps) {
  const { t } = useTranslation('quotes');

  const orderedStatuses: Array<QuoteStatus | 'total'> = [
    'preparation', 'sent', 'expired', 'approved', 'confirmed', 'inprogress',
    'waiting-po', 'invoice-ready', 'partially-invoiced', 'invoiced',
    'cancelled', 'total',
  ];

  const orderedStats = orderedStatuses
    .map((status) => {
      const stat = stats.find((s) => s.status === status);
      if (!stat) return null;
      return { ...stat, config: statusConfig[status] };
    })
    .filter(Boolean);

  if (orderedStats.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-1', className)}>
      {isApproximate && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5 text-xs text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5" />
                <span>{t('stats.approximate', 'Values are approximate due to large dataset')}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('stats.approximateTooltip', 'Only the first 1,000 quotes were included in these totals. The actual values may differ.')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-11 gap-3">
        {orderedStats.map((stat) => {
          if (!stat) return null;
          return (
            <Card key={stat.status} className="py-2">
              <CardHeader className="pb-1 px-3 pt-0">
                <CardTitle className="text-xs font-medium text-muted-foreground truncate">
                  {t(stat.config.i18nKey)}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-0">
                <div className={cn('text-lg font-bold', stat.config.valueColor)}>
                  {isApproximate ? '~' : ''}{stat.quantity.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {isApproximate ? '~' : ''}{formatStatCurrency(stat.amount)}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
